// ABOUTME: Modal for adding new child to family with birth info and optional profile details
// ABOUTME: Collects name, birth year/month, gender, and optional mantra/questions

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { AddChildFormState } from '../types';
import NicknamesChipInput from '../../common/NicknamesChipInput';

interface AddChildModalProps {
  open: boolean;
  form: AddChildFormState;
  onClose: () => void;
  onChange: (form: AddChildFormState) => void;
  onSubmit: () => Promise<void>;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const AddChildModal: React.FC<AddChildModalProps> = ({
  open,
  form,
  onClose,
  onChange,
  onSubmit
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const years = Array.from({length: 25}, (_, i) => currentYear - i);

  const handleYearChange = (newYear: number) => {
    const updated: AddChildFormState = { ...form, birthYear: newYear };
    // Auto-clear birth month if switching to current year and month is now in the future
    if (newYear === currentYear && form.birthMonth) {
      const monthNum = MONTH_NAMES.indexOf(form.birthMonth) + 1;
      if (monthNum > currentMonth) {
        updated.birthMonth = '';
      }
    }
    onChange(updated);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add Child</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            fullWidth
            required
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
          />
          <NicknamesChipInput
            value={form.aliases}
            onChange={(aliases) => onChange({ ...form, aliases })}
            childName={form.name}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150, flex: 1 }} variant="outlined">
              <InputLabel id="childBirthYear-label">Birth Year</InputLabel>
              <Select
                labelId="childBirthYear-label"
                id="childBirthYear"
                value={form.birthYear || ''}
                onChange={(e) => handleYearChange(parseInt(String(e.target.value)) || 0)}
                label="Birth Year"
                required
              >
                {years.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150, flex: 1 }} variant="outlined" required>
              <InputLabel id="childBirthMonth-label">Birth Month</InputLabel>
              <Select
                labelId="childBirthMonth-label"
                id="childBirthMonth"
                value={form.birthMonth}
                onChange={(e) => onChange({ ...form, birthMonth: e.target.value })}
                label="Birth Month"
              >
                {MONTH_NAMES.map((monthName, index) => {
                  const monthNum = index + 1;
                  const isDisabled = form.birthYear === currentYear && monthNum > currentMonth;
                  return (
                    <MenuItem key={monthName} value={monthName} disabled={isDisabled}>
                      {monthName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
          <FormControl fullWidth>
            <InputLabel>Gender (optional)</InputLabel>
            <Select
              label="Gender (optional)"
              value={form.gender}
              onChange={(e) => onChange({ ...form, gender: e.target.value })}
            >
              <MenuItem value="">Prefer not to say</MenuItem>
              <MenuItem value="Boy">Boy</MenuItem>
              <MenuItem value="Girl">Girl</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Personal Mantra (optional)"
            fullWidth
            multiline
            rows={2}
            value={form.mantra}
            onChange={(e) => onChange({ ...form, mantra: e.target.value })}
          />
          <TextField
            label="What do you love about your family? (optional)"
            fullWidth
            multiline
            rows={2}
            value={form.familyLoveQuestion}
            onChange={(e) => onChange({ ...form, familyLoveQuestion: e.target.value })}
          />
          <TextField
            label="What does your family need from you? (optional)"
            fullWidth
            multiline
            rows={2}
            value={form.familyNeedQuestion}
            onChange={(e) => onChange({ ...form, familyNeedQuestion: e.target.value })}
          />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Upload Avatar
            </Typography>
            <Button variant="outlined">Choose File</Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!form.name.trim() || !form.birthMonth.trim() || form.birthYear <= 0}
        >
          Add Child
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddChildModal;
